// Flexible connection & curtailment (spec Feature 6).
//
// Mirrors the backend `BESS_feasibility/Configuration/connection_curtailment.py`
// so the payload emits exactly the keys `normalize_connection_curtailment_config()`
// expects. Keep the two files in sync — the backend is the source of truth for
// the accuracy gate, and this file only exists to make the gate visible to the
// user BEFORE they generate a report they cannot defend.
//
// Applicability (spec section 3): merchant / tolling / PPA. NOT behind-the-meter
// peak shaving or ToU — those savings come from a site load, not from injected
// energy, so a grid curtailment instruction does not touch them. The section is
// therefore never rendered for settlementType "BTM".
//
// Neutral defaults: no curtailment, no securities, no COD delay. A project that
// never opens this section produces exactly the numbers it produced before.

export const CONNECTION_TYPE_OPTIONS = [
  {
    value: "unspecified",
    label: "Not specified — to verify against the connection offer",
    description:
      "The report will state that the connection terms have not been confirmed and " +
      "will model no curtailment. This is an assumption, not a finding, and it is " +
      "labelled as such wherever it appears.",
  },
  {
    value: "firm",
    label: "Firm",
    description:
      "The connection capacity is guaranteed. No curtailment is modelled in the base " +
      "case unless the connection offer states otherwise.",
  },
  {
    value: "non_firm",
    label: "Non-firm / flexible",
    description:
      "The operator may limit injection. If you have the expected curtailment from the " +
      "connection offer or a grid study, enter it below and it will reduce injected " +
      "energy before revenue. If you do not, the report shows a labelled 0 / 3 / 5 % " +
      "scenario band instead of inventing a figure.",
  },
];

export const CURTAILMENT_INPUT_MODE_OPTIONS = [
  { value: "percent_of_annual_energy", label: "Share of annual injected energy (%)" },
  { value: "hours_per_year", label: "Curtailed hours per year (h/yr)" },
];

// Only the first two count as evidence. This is the accuracy gate, and it is
// deliberately visible in the dropdown rather than hidden in the backend: the
// user should see that "internal assumption" will not move the base case.
export const CURTAILMENT_SOURCE_OPTIONS = [
  { value: "not_provided", label: "No source yet", evidenced: false },
  { value: "connection_offer", label: "Connection offer / terms of connection", evidenced: true },
  { value: "grid_study", label: "Grid connection study", evidenced: true },
  { value: "operator_guidance", label: "Operator guidance / published statistics", evidenced: false },
  { value: "internal_assumption", label: "Internal assumption (not evidenced)", evidenced: false },
];

// Forfeited is listed first and is the default. Greek letters of guarantee are
// usually released, so "refundable" is often the true answer — but an UNSTATED
// treatment is the uncertain case, and spec section 2 says be conservative when
// uncertain. Assuming a security comes back when nobody has confirmed it
// understates the funding cost by nearly its full face value.
export const SECURITIES_TREATMENT_OPTIONS = [
  {
    value: "forfeited",
    label: "Forfeited / non-refundable (assumed when not stated)",
    description: "Never returned. The full amount is a sunk cost and hits the NPV.",
  },
  {
    value: "refundable",
    label: "Refundable — released back to the project at COD",
    description:
      "Funded on day one and returned at COD, so the cost to the project is the carry " +
      "(one year of discounting), not the face value. Select this only if the " +
      "connection terms actually provide for release.",
  },
];

export const CONNECTION_CURTAILMENT_HELPER_TEXT =
  "Connection risk belongs in the numbers, not in a footnote. Curtailment entered here " +
  "reduces injected energy BEFORE revenue is derived from it, so it shows up in the " +
  "energy balance and the LCOE as well as in the cash flow. A curtailment figure only " +
  "reaches the base case if it comes from the actual connection offer or a grid study; " +
  "anything else is shown as a clearly-labelled scenario, because an unsourced number " +
  "in a bank report is worse than no number at all.";

export const REGULATORY_CAP_NOTE =
  "This is separate from the Category 11A/11B injection cap. That cap is a known limit " +
  "the dispatch optimisation already schedules around, and its spilled energy is inside " +
  "the dispatch figures. What you enter here is an unforeseeable instruction from the " +
  "network operator. The report shows both and states that they are not double-counted.";

export const DEFAULT_CONNECTION_CURTAILMENT = {
  connectionType: "unspecified",
  curtailmentInputMode: "percent_of_annual_energy",
  curtailmentPct: 0,
  curtailmentHoursPerYear: 0,
  curtailmentSource: "not_provided",
  curtailmentSourceReference: "",
  securitiesEur: 0,
  securitiesTreatment: "forfeited",
  expectedCodDate: "",
  codDelayScenarioMonths: 0,
};

const HOURS_PER_YEAR = 8760;

export function isEvidencedSource(source) {
  return ["connection_offer", "grid_study"].includes(String(source || ""));
}

/** Energy fraction lost, matching `_resolve_fraction` in the backend. */
export function curtailmentFraction(cc) {
  if (!cc) return 0;
  if (cc.curtailmentInputMode === "hours_per_year") {
    const hours = Math.max(0, Number(cc.curtailmentHoursPerYear) || 0);
    return Math.min(hours / HOURS_PER_YEAR, 0.9);
  }
  const pct = Math.max(0, Number(cc.curtailmentPct) || 0);
  return Math.min(pct / 100, 0.9);
}

/** What the report will actually apply, after the accuracy gate. */
export function appliedCurtailmentFraction(cc) {
  return isEvidencedSource(cc?.curtailmentSource) ? curtailmentFraction(cc) : 0;
}

export function showsSensitivityBand(cc) {
  return Boolean(
    cc && cc.connectionType === "non_firm" && !isEvidencedSource(cc.curtailmentSource)
  );
}

/** True when this settlement type is in scope at all (spec section 3). */
export function connectionCurtailmentApplies(settlementType) {
  return String(settlementType || "") !== "BTM";
}

export function normalizeConnectionCurtailment(cfg) {
  const raw =
    (cfg && cfg.connectionCurtailment) ||
    (cfg && cfg.feasibility?.economics?.connectionCurtailment) ||
    {};
  const legacy = (cfg && cfg.feasibility?.economics?.financialParams) || {};
  const merged = { ...DEFAULT_CONNECTION_CURTAILMENT, ...raw };

  // Honour the legacy Batch-2 fields so a stored config opens with the values
  // the user already entered instead of silently resetting them.
  if (raw.connectionType === undefined && legacy.gridConnectionFirmness) {
    const legacyType = String(legacy.gridConnectionFirmness).toLowerCase().replace(/-/g, "_");
    if (legacyType === "firm" || legacyType === "non_firm" || legacyType === "nonfirm") {
      merged.connectionType = legacyType === "nonfirm" ? "non_firm" : legacyType;
    }
  }
  if (raw.curtailmentPct === undefined && legacy.curtailmentPct !== undefined) {
    merged.curtailmentPct = Number(legacy.curtailmentPct) || 0;
  }
  if (raw.securitiesEur === undefined && legacy.connectionSecuritiesEur !== undefined) {
    merged.securitiesEur = Number(legacy.connectionSecuritiesEur) || 0;
  }
  return merged;
}

export function validateConnectionCurtailment(cc, settlementType) {
  const errors = {};
  if (!cc || !connectionCurtailmentApplies(settlementType)) return errors;

  const pct = Number(cc.curtailmentPct);
  if (cc.curtailmentInputMode === "percent_of_annual_energy" && cc.curtailmentPct !== "" ) {
    if (!Number.isFinite(pct) || pct < 0) {
      errors.curtailmentPct = "Curtailment cannot be negative.";
    } else if (pct > 90) {
      errors.curtailmentPct =
        "Curtailment above 90% is outside the modelling range — re-check the connection terms.";
    }
  }

  const hours = Number(cc.curtailmentHoursPerYear);
  if (cc.curtailmentInputMode === "hours_per_year" && cc.curtailmentHoursPerYear !== "") {
    if (!Number.isFinite(hours) || hours < 0) {
      errors.curtailmentHoursPerYear = "Curtailed hours cannot be negative.";
    } else if (hours > HOURS_PER_YEAR) {
      errors.curtailmentHoursPerYear = `There are only ${HOURS_PER_YEAR} hours in a year.`;
    }
  }

  if (isEvidencedSource(cc.curtailmentSource) && !String(cc.curtailmentSourceReference || "").trim()) {
    errors.curtailmentSourceReference =
      "Cite the document (e.g. “HEDNO connection offer 2026-03-11, clause 7.2”). " +
      "Every figure in the report carries its source.";
  }

  const securities = Number(cc.securitiesEur);
  if (cc.securitiesEur !== "" && (!Number.isFinite(securities) || securities < 0)) {
    errors.securitiesEur = "Connection securities cannot be negative.";
  }

  const months = Number(cc.codDelayScenarioMonths);
  if (cc.codDelayScenarioMonths !== "" && (!Number.isFinite(months) || months < 0 || months > 60)) {
    errors.codDelayScenarioMonths = "Enter a COD delay scenario between 0 and 60 months.";
  }

  if (cc.expectedCodDate && !/^\d{4}-\d{2}-\d{2}$/.test(String(cc.expectedCodDate))) {
    errors.expectedCodDate = "Use the format YYYY-MM-DD.";
  }
  return errors;
}

/** Non-blocking notices shown inline, mirroring the backend's warnings. */
export function connectionCurtailmentNotices(cc) {
  const notices = [];
  if (!cc) return notices;
  const fraction = curtailmentFraction(cc);
  const evidenced = isEvidencedSource(cc.curtailmentSource);

  if (fraction > 0 && !evidenced) {
    notices.push({
      severity: "warning",
      text:
        `You entered ${(fraction * 100).toFixed(1)}% curtailment without a connection offer or ` +
        "grid study behind it. The report will show it as a labelled scenario and will NOT " +
        "reduce the base case. Select the connection offer or grid study as the source to " +
        "bring it into the numbers.",
    });
  }
  if (cc.connectionType === "non_firm" && !evidenced) {
    notices.push({
      severity: "info",
      text:
        "Non-firm connection without an evidenced curtailment figure: the report will show a " +
        "0 / 3 / 5 % scenario band and keep the base case at zero curtailment.",
    });
  }
  if (cc.connectionType === "firm" && fraction > 0 && evidenced) {
    notices.push({
      severity: "warning",
      text:
        "A firm connection is declared yet curtailment is expected. Confirm whether the " +
        "curtailment is compensated under the connection terms.",
    });
  }
  if (cc.connectionType === "unspecified") {
    notices.push({
      severity: "info",
      text:
        "Connection type not stated. The report will model no curtailment and will say so " +
        "explicitly — confirm against the connection offer before relying on it.",
    });
  }
  if (cc.curtailmentInputMode === "hours_per_year" && Number(cc.curtailmentHoursPerYear) > 0) {
    notices.push({
      severity: "info",
      text:
        "Hours are converted on a flat 8,760-hour basis. Curtailment concentrated in " +
        "high-output hours would cost more energy than this, so the report discloses the basis.",
    });
  }
  if (Number(cc.securitiesEur) > 0) {
    notices.push({
      severity: "info",
      text:
        cc.securitiesTreatment === "refundable"
          ? "Refundable securities are funded on day one and released at COD; only the carry " +
            "affects the NPV. Confirm the connection terms provide for release."
          : "Forfeited securities are booked as a sunk cost and hit the NPV in full. This is the " +
            "conservative default — switch to refundable only if the guarantee is genuinely released.",
    });
  }
  return notices;
}

/** Emit exactly the block the backend normalizer reads. */
export function buildConnectionCurtailmentPayload(cc, settlementType) {
  if (!connectionCurtailmentApplies(settlementType)) return undefined;
  const normalized = normalizeConnectionCurtailment({ connectionCurtailment: cc });
  return {
    connectionType: normalized.connectionType,
    curtailmentInputMode: normalized.curtailmentInputMode,
    curtailmentPct: Number(normalized.curtailmentPct) || 0,
    curtailmentHoursPerYear: Number(normalized.curtailmentHoursPerYear) || 0,
    curtailmentSource: normalized.curtailmentSource,
    curtailmentSourceReference: String(normalized.curtailmentSourceReference || "").trim(),
    securitiesEur: Number(normalized.securitiesEur) || 0,
    securitiesTreatment: normalized.securitiesTreatment,
    expectedCodDate: String(normalized.expectedCodDate || "").trim(),
    codDelayScenarioMonths: Number(normalized.codDelayScenarioMonths) || 0,
  };
}
